import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { theme } from 'styles/theme';
import api from 'services/api';
import { showAlert } from 'utils/toastBridge';

const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const PHOTO_MAX_BYTES = 500 * 1024;

export const PhotoBox = styled.button`
  flex-shrink: 0;
  width: 120px;
  height: 150px;
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.background.secondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 12px;
  line-height: 1.4;
  color: ${theme.colors.text.tertiary};
  padding: 0;
  box-sizing: border-box;
  cursor: pointer;
  overflow: hidden;
  position: relative;

  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const PhotoActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 6px;
  width: 120px;
  justify-content: center;
`;

export const PhotoActionBtn = styled.button`
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.sm};
  font-size: 11px;
  padding: 2px 8px;
  cursor: pointer;
  color: ${theme.colors.text.secondary};

  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * 단건 사진 등록/미리보기
 * - 저장된 사원(사번 있음): 즉시 multipart 업로드 → photoPath 갱신
 * - 신규: 로컬 미리보기만 (저장 후 업로드 안내)
 */
const PhotoUploadField = ({ tenantCode }) => {
  const { watch, setValue, getValues } = useFormContext();
  const photoPath = watch('photoPath');
  const empNo = watch('empNo');
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const blobUrlRef = useRef(null);

  const revokeBlob = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    revokeBlob();
    setPreviewUrl('');

    const load = async () => {
      if (!photoPath || !tenantCode || !empNo) return;
      try {
        const { data, status } = await api.getBlob(
          `/hr/employees/photo?tenantCode=${encodeURIComponent(tenantCode)}&empNo=${encodeURIComponent(empNo)}`
        );
        if (cancelled || status === 404 || !data) return;
        const url = URL.createObjectURL(data);
        blobUrlRef.current = url;
        setPreviewUrl(url);
      } catch {
        if (!cancelled) setPreviewUrl('');
      }
    };
    load();
    return () => {
      cancelled = true;
      revokeBlob();
    };
  }, [photoPath, tenantCode, empNo, revokeBlob]);

  const openPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const onFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      if (!String(file.type || '').startsWith('image/')) {
        showAlert('이미지 파일만 등록할 수 있습니다.', 'warning');
        return;
      }
      if (file.size > PHOTO_MAX_BYTES) {
        showAlert('사진 용량은 500KB 이하만 등록할 수 있습니다.', 'warning');
        return;
      }

      const currentEmpNo = (getValues('empNo') || '').trim();
      if (!currentEmpNo) {
        showAlert('사번을 먼저 입력한 뒤 사진을 등록해 주세요.', 'warning');
        return;
      }
      if (!tenantCode) {
        showAlert('기관코드가 필요합니다.', 'warning');
        return;
      }

      // 신규 미저장 사원: 로컬 미리보기만
      const personId = getValues('__personId'); // not used
      void personId;

      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('tenantCode', tenantCode);
        formData.append('empNo', currentEmpNo);
        formData.append('file', file);

        const { data } = await api.postForm('/hr/employees/photo', formData);
        if (data.Msg === 'success' || data.success) {
          setValue('photoPath', data.photoPath || `uploaded:${Date.now()}`, { shouldDirty: true });
          showAlert('사진이 등록되었습니다.', 'success');
        } else {
          showAlert(data.Msg || '사진 등록에 실패했습니다.', 'error');
        }
      } catch (err) {
        // 사번 미존재(신규)면 로컬 미리보기
        const msg = err?.data?.Msg || err?.message || '';
        if (String(msg).includes('사번을 찾을 수 없습니다')) {
          revokeBlob();
          const url = URL.createObjectURL(file);
          blobUrlRef.current = url;
          setPreviewUrl(url);
          setValue('photoPath', '', { shouldDirty: true });
          setValue('_pendingPhotoFile', file, { shouldDirty: true });
          showAlert('사원 저장 후 사진이 서버에 등록됩니다. (미리보기만 표시)', 'warning');
        } else {
          showAlert(msg || '사진 등록에 실패했습니다.', 'error');
        }
      } finally {
        setUploading(false);
      }
    },
    [getValues, revokeBlob, setValue, tenantCode]
  );

  const clearPhoto = useCallback(
    (e) => {
      e.stopPropagation();
      revokeBlob();
      setPreviewUrl('');
      setValue('photoPath', '', { shouldDirty: true });
      setValue('_pendingPhotoFile', null, { shouldDirty: true });
    },
    [revokeBlob, setValue]
  );

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        style={{ display: 'none' }}
        onChange={onFileChange}
      />
      <PhotoBox type="button" onClick={openPicker} disabled={uploading} title="클릭하여 사진 등록">
        {previewUrl ? (
          <img src={previewUrl} alt="사원 사진" />
        ) : (
          <>
            {uploading ? '업로드 중…' : '사진 등록'}
            <br />
            (클릭)
          </>
        )}
      </PhotoBox>
      {previewUrl ? (
        <PhotoActions>
          <PhotoActionBtn type="button" onClick={openPicker} disabled={uploading}>
            변경
          </PhotoActionBtn>
          <PhotoActionBtn type="button" onClick={clearPhoto} disabled={uploading}>
            삭제
          </PhotoActionBtn>
        </PhotoActions>
      ) : null}
    </div>
  );
};

export default PhotoUploadField;
